import React, { Component } from 'react';
import { getLocationName, ALL_LOCATIONS_ID, ALL_DEALERSHIPS_ID, isAllLocations, isAllDealerships, isMobileLocation } from '../../util/locationUtils';
import ScopeService from '../../services/scopeService';
import { isViewerOrTechnician } from '../../util/permissions';

/**
 * Unified company / location selector.
 *
 * Reads the user's scope tree from ScopeService.getAllScopes(). Each scope has
 * a uniform shape: { type, id, name, role, scopedId, locations?, dealerships? }.
 * `scope.type` is only 'installer' or 'dealer' (matches the server
 * user_companies.company_type enum). Sub-scoping comes from `scope.scopedId`,
 * which mirrors user_companies.location_id (0 = full access; > 0 = pinned to
 * that single location_id / dealer_id).
 *
 * Scope rendering:
 *   - installer + scopedId=0   → parent row + all locations
 *   - installer + scopedId>0   → single leaf (the pinned location, already filtered server-side)
 *   - dealer    + scopedId=0   → parent row + all dealerships in the group
 *   - dealer    + scopedId>0   → single leaf (the pinned dealership)
 *
 * Selection paths:
 *   - Cross-scope switch    → onSwitch(scopeIndex, targetSubId?)  ← App.handleSwitchScope
 *   - Same-scope sub-select →
 *       installer → onLocationChange(locationId)                  ← App.handleLocationChange
 *       dealer    → ScopeService.setSelectedDealerId + reload     (handled in-component)
 */
class CompanyLocationSelector extends Component {
  state = {
    open: false,
  };

  toggleOpen = () => {
    this.setState(prev => ({ open: !prev.open }));
  };

  close = () => {
    this.setState({ open: false });
  };

  // Active scope = where the user currently is
  getActiveScopeIndex = () => {
    return ScopeService.getActiveScopeIndex() ?? 0;
  };

  getActiveScope = () => {
    return ScopeService.getActiveScope();
  };

  getAllScopes = () => {
    return ScopeService.getAllScopes();
  };

  getActiveSubId = () => {
    const scope = this.getActiveScope();
    if (!scope) return null;
    if (scope.type === 'installer') return ScopeService.getSelectedLocationId();
    if (scope.type === 'dealer') return ScopeService.getSelectedDealerId();
    return null;
  };

  // Sentinel for "no sub-scope selected" (varies by type)
  allSubIdFor = (scope) => {
    if (!scope) return null;
    if (scope.type === 'installer') return ALL_LOCATIONS_ID;
    if (scope.type === 'dealer') return ALL_DEALERSHIPS_ID;
    return null;
  };

  isAllSubSelected = (scope, subId) => {
    if (!scope) return true;
    if (scope.type === 'installer') return isAllLocations(subId);
    if (scope.type === 'dealer') return isAllDealerships(subId);
    return true;
  };

  // ------------------------------------------------------------------
  // Mode resolution
  // ------------------------------------------------------------------

  getMode = () => {
    const role = (ScopeService.getActiveScopeRole() || ScopeService.getUserRole() || '').toLowerCase();
    if (isViewerOrTechnician(role)) return 'static';

    const scopes = this.getAllScopes();
    if (scopes.length >= 2) return 'grouped';

    // Single scope — flat dropdown if it has multiple children
    const scope = scopes[0] || this.getActiveScope();
    if (this.scopeChildCount(scope) >= 2) return 'flat';

    return 'static';
  };

  scopeChildCount = (scope) => {
    if (!scope) return 0;
    if (scope.type === 'installer') return (scope.locations || []).length;
    if (scope.type === 'dealer') return (scope.dealerships || []).length;
    return 0;
  };

  // ------------------------------------------------------------------
  // Display
  // ------------------------------------------------------------------

  getDisplayValue = () => {
    const scope = this.getActiveScope();
    if (!scope) return this.props.companyName || '';

    const subId = this.getActiveSubId();

    if (scope.type === 'installer') {
      if (isAllLocations(subId)) return scope.name;
      const loc = (scope.locations || []).find(l => String(l.location_id) === String(subId));
      return loc ? `${scope.name} - ${getLocationName(loc)}` : scope.name;
    }

    if (scope.type === 'dealer') {
      if (isAllDealerships(subId)) return scope.name;
      const dealer = (scope.dealerships || []).find(d => String(d.dealer_id) === String(subId));
      return dealer ? `${scope.name} - ${dealer.dealer_name}` : scope.name;
    }

    return scope.name;
  };

  getStaticDisplayValue = () => {
    const scope = this.getActiveScope();
    if (!scope) return this.props.companyName || '';

    if (scope.type === 'installer') {
      const locs = scope.locations || [];
      const subId = this.getActiveSubId();
      if (!isAllLocations(subId)) {
        const loc = locs.find(l => String(l.location_id) === String(subId));
        if (loc) return `${scope.name} - ${getLocationName(loc)}`;
      }
      if (locs.length === 1) return `${scope.name} - ${getLocationName(locs[0])}`;
    }

    return scope.name;
  };

  // ------------------------------------------------------------------
  // Selection
  // ------------------------------------------------------------------

  /**
   * Click handler for a scope row (parent or leaf).
   * - Same active scope, root row click → clear sub-selection
   * - Different scope → cross-scope switch via App.handleSwitchScope
   */
  selectScope = (scopeIndex, targetSubId = null) => {
    this.close();
    const activeIdx = this.getActiveScopeIndex();
    const scope = this.getAllScopes()[scopeIndex];
    if (!scope) return;

    if (scopeIndex === activeIdx) {
      // Same scope — sub-scope change only
      this.applySameScopeSub(scope, targetSubId);
      this.afterSelect();
      return;
    }

    // Different scope — full switch
    if (this.props.onSwitch) {
      this.props.onSwitch(scopeIndex, targetSubId);
    }
    this.afterSelect();
  };

  applySameScopeSub = (scope, targetSubId) => {
    if (scope.type === 'installer') {
      const target = targetSubId == null ? ALL_LOCATIONS_ID : targetSubId;
      const currentLoc = ScopeService.getSelectedLocationId();
      if (String(target) === String(currentLoc)) return;
      // Lightweight path through App.handleLocationChange (writes companyAPI + reloads)
      if (this.props.onLocationChange) this.props.onLocationChange(target);
      return;
    }
    if (scope.type === 'dealer') {
      const target = targetSubId == null ? ALL_DEALERSHIPS_ID : targetSubId;
      const currentDealer = ScopeService.getSelectedDealerId();
      if (String(target) === String(currentDealer)) return;
      ScopeService.setSelectedDealerId(target);
      window.location.reload();
    }
  };

  afterSelect = () => {
    if (this.props.onAfterSelect) this.props.onAfterSelect();
  };

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  renderStatic = () => {
    return (
      <span className="navbar-brand mb-0">{this.getStaticDisplayValue()}</span>
    );
  };

  renderFlatDropdown = () => {
    const { open } = this.state;
    const variant = this.props.variant || 'desktop';
    const scope = this.getAllScopes()[0] || this.getActiveScope();
    if (!scope) return this.renderStatic();
    const subId = this.getActiveSubId();
    const allSelected = this.isAllSubSelected(scope, subId);

    const children = scope.type === 'installer'
      ? (scope.locations || []).map(loc => ({
          id: loc.location_id,
          label: `${scope.name} - ${getLocationName(loc)}`,
          isMobile: isMobileLocation(loc),
        }))
      : scope.type === 'dealer'
      ? (scope.dealerships || []).map(d => ({
          id: d.dealer_id,
          label: `${scope.name} - ${d.dealer_name}`,
          isMobile: false,
        }))
      : [];

    const allLabel = scope.type === 'dealer' ? 'All Dealerships' : 'All Locations';

    return (
      <div className={`cl-selector cl-variant-${variant}`} style={{ position: 'relative', display: 'inline-block' }}>
        <span
          className={`navbar-brand mb-0 cl-toggle${open ? ' open' : ''}`}
          onClick={this.toggleOpen}
        >
          {this.getDisplayValue()}
          <i className="fa-sharp fa-solid fa-chevron-down cl-chevron"></i>
        </span>

        {open && (
          <>
            <div className="cl-backdrop" onClick={this.close} />
            <div className="cl-menu">
              <div
                className={`cl-item${allSelected ? ' active' : ''}`}
                onClick={() => this.selectScope(0, null)}
              >
                {allLabel}
              </div>
              <div className="cl-divider" />
              {children.map(child => {
                const isSelected = String(child.id) === String(subId);
                return (
                  <div
                    key={child.id}
                    className={`cl-item${isSelected ? ' active' : ''}`}
                    onClick={() => this.selectScope(0, child.id)}
                  >
                    {child.isMobile && (
                      <i className="fa-sharp fa-regular fa-truck mr-2" aria-label="Mobile crew" title="Mobile crew" />
                    )}
                    {child.label}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  renderGroupedDropdown = () => {
    const { open } = this.state;
    const variant = this.props.variant || 'desktop';
    const scopes = this.getAllScopes();
    const activeIdx = this.getActiveScopeIndex();
    const activeSubId = this.getActiveSubId();

    return (
      <div className={`cl-selector cl-variant-${variant}`} style={{ position: 'relative', display: 'inline-block' }}>
        <span
          className={`navbar-brand mb-0 cl-toggle${open ? ' open' : ''}`}
          onClick={this.toggleOpen}
        >
          {this.getDisplayValue()}
          <i className="fa-sharp fa-solid fa-chevron-down cl-chevron"></i>
        </span>

        {open && (
          <>
            <div className="cl-backdrop" onClick={this.close} />
            <div className="cl-menu">
              {scopes.map((scope, idx) => {
                const isActiveScope = idx === activeIdx;
                const childCount = this.scopeChildCount(scope);
                const allSubSelected = isActiveScope && this.isAllSubSelected(scope, activeSubId);

                // Leaf scope — single clickable row
                if (childCount === 0) {
                  return (
                    <React.Fragment key={`scope-${idx}`}>
                      {idx > 0 && <div className="cl-divider" />}
                      <div
                        className={`cl-item cl-company-item${isActiveScope ? ' active' : ''}`}
                        onClick={() => this.selectScope(idx, null)}
                      >
                        {scope.name}
                      </div>
                    </React.Fragment>
                  );
                }

                // Single-child scope — render the child row only (no separate parent)
                if (childCount === 1) {
                  const onlyChild = scope.type === 'installer' ? scope.locations[0] : scope.dealerships[0];
                  const childId = scope.type === 'installer' ? onlyChild.location_id : onlyChild.dealer_id;
                  const childLabel = scope.type === 'installer'
                    ? `${scope.name} - ${getLocationName(onlyChild)}`
                    : `${scope.name} - ${onlyChild.dealer_name}`;
                  const childActive = isActiveScope && (allSubSelected || String(activeSubId) === String(childId));
                  return (
                    <React.Fragment key={`scope-${idx}`}>
                      {idx > 0 && <div className="cl-divider" />}
                      <div
                        className={`cl-item cl-company-item${childActive ? ' active' : ''}`}
                        onClick={() => this.selectScope(idx, childId)}
                      >
                        {childLabel}
                      </div>
                    </React.Fragment>
                  );
                }

                // Multi-child scope — parent + child rows
                const childRows = scope.type === 'installer'
                  ? (scope.locations || []).map(loc => ({
                      id: loc.location_id,
                      label: `${scope.name} - ${getLocationName(loc)}`,
                      isMobile: isMobileLocation(loc),
                    }))
                  : (scope.dealerships || []).map(d => ({
                      id: d.dealer_id,
                      label: `${scope.name} - ${d.dealer_name}`,
                      isMobile: false,
                    }));

                return (
                  <React.Fragment key={`scope-${idx}`}>
                    {idx > 0 && <div className="cl-divider" />}
                    <div
                      className={`cl-item cl-company-item${allSubSelected ? ' active' : ''}`}
                      onClick={() => this.selectScope(idx, null)}
                    >
                      {scope.name}
                    </div>
                    {childRows.map(child => {
                      const isSelected = isActiveScope && String(activeSubId) === String(child.id);
                      return (
                        <div
                          key={child.id}
                          className={`cl-item cl-location-item${isSelected ? ' active' : ''}`}
                          onClick={() => this.selectScope(idx, child.id)}
                        >
                          {child.isMobile && (
                            <i className="fa-sharp fa-regular fa-truck mr-2" aria-label="Mobile crew" title="Mobile crew" />
                          )}
                          {child.label}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  render() {
    const mode = this.getMode();
    switch (mode) {
      case 'grouped':
        return this.renderGroupedDropdown();
      case 'flat':
        return this.renderFlatDropdown();
      case 'static':
      default:
        return this.renderStatic();
    }
  }
}

export default CompanyLocationSelector;
